import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Plus, AlertTriangle, Clock, Loader2 } from "lucide-react";
import EntityFormDialog from "@/components/EntityFormDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function StatoPill({ stato }) {
  if (stato === "inviata") return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Inviata</span>;
  if (stato === "bozza") return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Bozza</span>;
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Mancante</span>;
}

export default function Relazioni() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStato, setFilterStato] = useState("");
  const [filterMese, setFilterMese] = useState("");
  const [filterAnno, setFilterAnno] = useState(new Date().getFullYear());
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ hotel_id: "", mese: new Date().getMonth() + 1, anno: new Date().getFullYear(), titolo: "" });
  const [saving, setSaving] = useState(false);

  const isRm = user?.role === "resident_manager";
  const today = new Date();
  const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([api.get("/reports"), api.get("/hotels")]);
      setReports(r.data || []);
      setHotels(h.data || []);
      if (user && (user.role === "super_admin" || user.role === "capo_area")) {
        try {
          const m = await api.get(`/reports/check-missing?anno=${prevYear}&mese=${prevMonth}`);
          setMissing(m.data || []);
        } catch {}
      }
      if (isRm && h.data?.length && !createForm.hotel_id) {
        setCreateForm((f) => ({ ...f, hotel_id: h.data[0].id }));
      }
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user]);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (filterStato && r.stato !== filterStato) return false;
      if (filterMese && r.mese !== parseInt(filterMese)) return false;
      if (filterAnno && r.anno !== parseInt(filterAnno)) return false;
      return true;
    });
  }, [reports, filterStato, filterMese, filterAnno]);

  const nomeHotel = (id) => hotels.find((h) => h.id === id)?.nome || "—";

  const curMonthReport = reports.find((r) => r.mese === today.getMonth() + 1 && r.anno === today.getFullYear() && (!isRm || r.resident_manager_id === user.id));
  const prevMonthReport = reports.find((r) => r.mese === prevMonth && r.anno === prevYear && (!isRm || r.resident_manager_id === user.id));

  const createReport = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/reports", createForm);
      toast.success("Bozza creata");
      setCreateOpen(false);
      navigate(`/relazioni/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="relazioni-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Relazioni mensili</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Resident <span className="font-medium">Report</span></h1>
        </div>
        {isRm && (
          <button onClick={() => setCreateOpen(true)} data-testid="new-report-btn" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova relazione</button>
        )}
      </div>

      {/* RM scadenza box */}
      {isRm && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rm-scadenze">
          <div className="ih-glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Mese corrente · {MESI[today.getMonth()]} {today.getFullYear()}</div>
            {curMonthReport ? (
              <div className="flex items-center justify-between">
                <span className="text-sm">{curMonthReport.titolo}</span>
                <StatoPill stato={curMonthReport.stato} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nessuna relazione iniziata. Crea bozza →</p>
            )}
          </div>
          <div className={`ih-glass rounded-2xl p-5 ${!prevMonthReport ? "border-rose-500/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Mese precedente · {MESI[prevMonth - 1]} {prevYear}</div>
            {prevMonthReport ? (
              <div className="flex items-center justify-between">
                <span className="text-sm">{prevMonthReport.titolo}</span>
                <StatoPill stato={prevMonthReport.stato} />
              </div>
            ) : (
              <p className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Relazione mancante — invio richiesto.</p>
            )}
          </div>
        </div>
      )}

      {/* Missing reports (super_admin / capo_area) */}
      {missing.length > 0 && (
        <div className="ih-glass rounded-2xl p-6 border-rose-500/30" data-testid="missing-panel">
          <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-rose-500" /><h2 className="font-display text-lg tracking-tight">Mancanti per {MESI[prevMonth - 1]} {prevYear}</h2></div>
          <div className="space-y-2">
            {missing.map((m) => (
              <div key={m.hotel.id} className="rounded-xl border border-border/60 p-3 flex items-center gap-3 bg-rose-50/40 dark:bg-rose-900/10">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{m.hotel.nome}</div>
                  <div className="text-[11px] text-muted-foreground">RM: {m.resident_manager.nome} {m.resident_manager.cognome}</div>
                </div>
                <StatoPill stato="mancante" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ih-glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <select data-testid="filter-stato" value={filterStato} onChange={(e) => setFilterStato(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti gli stati</option>
          <option value="bozza">Bozza</option>
          <option value="inviata">Inviata</option>
        </select>
        <select data-testid="filter-mese" value={filterMese} onChange={(e) => setFilterMese(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <option value="">Tutti i mesi</option>
          {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input data-testid="filter-anno" type="number" value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)} className="rounded-lg border border-border bg-background px-3 py-2 text-sm w-24" />
      </div>

      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-lg tracking-tight mb-4">Storico relazioni</h2>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna relazione.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((r) => (
              <div key={r.id} onClick={() => navigate(`/relazioni/${r.id}`)} data-testid={`report-${r.id}`} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.titolo}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{nomeHotel(r.hotel_id)} · {MESI[r.mese - 1]} {r.anno}</div>
                </div>
                {r.data_invio && <div className="text-[11px] text-muted-foreground hidden sm:block">Inviata {new Date(r.data_invio).toLocaleDateString("it-IT")}</div>}
                <StatoPill stato={r.stato} />
              </div>
            ))}
          </div>
        )}
      </div>

      <EntityFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Nuova relazione"
        description="Crea una bozza di relazione mensile per il tuo hotel."
        size="md"
        onSubmit={createReport}
        submitLabel="Crea bozza"
        saving={saving}
        testId="new-report-modal"
        submitTestId="new-report-submit"
      >
        <div className="space-y-2">
          <Label>Hotel</Label>
          <Select value={createForm.hotel_id} onValueChange={(v) => setCreateForm({ ...createForm, hotel_id: v })}>
            <SelectTrigger><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
            <SelectContent>
              {hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Mese</Label>
            <Select value={String(createForm.mese)} onValueChange={(v) => setCreateForm({ ...createForm, mese: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Anno</Label>
            <Input type="number" value={createForm.anno} onChange={(e) => setCreateForm({ ...createForm, anno: parseInt(e.target.value) || createForm.anno })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Titolo <span className="text-muted-foreground text-xs">(opzionale)</span></Label>
          <Input placeholder="Es. Highlights di marzo" value={createForm.titolo} onChange={(e) => setCreateForm({ ...createForm, titolo: e.target.value })} />
        </div>
      </EntityFormDialog>
    </div>
  );
}
